import React from 'react';
import PropTypes from 'prop-types';
import { CheckIcon, PlusIcon } from '@heroicons/react/solid';

// Tap-to-mark attendance roster: one large toggle tile per person, so a cell
// of ~20 fits on one screen with no paging. Shared by Create and Edit Report.

const AVATAR_COLOURS = [
    'bg-emerald-100 text-emerald-800',
    'bg-sky-100 text-sky-800',
    'bg-amber-100 text-amber-800',
    'bg-rose-100 text-rose-800',
    'bg-violet-100 text-violet-800',
    'bg-teal-100 text-teal-800',
    'bg-orange-100 text-orange-800',
    'bg-indigo-100 text-indigo-800',
];

export function initialsOf(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Same person, same colour, every time.
export function colourFor(name) {
    const s = String(name || '');
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
}

const PersonTile = ({ person, onToggle }) => {
    const { id, name, present } = person;
    return (
        <button
            type="button"
            aria-pressed={present}
            aria-label={`${name}, ${present ? 'present' : 'not marked'}`}
            onClick={() => onToggle(id, !present)}
            className={`relative flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 ${present
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 bg-white hover:border-gray-300'}`}
        >
            {present && (
                <span className="absolute right-1.5 top-1.5 rounded-full bg-emerald-500 p-0.5 text-white" aria-hidden="true">
                    <CheckIcon className="h-3.5 w-3.5" />
                </span>
            )}
            <span
                aria-hidden="true"
                className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold ${present ? 'bg-emerald-500 text-white' : colourFor(name)}`}
            >
                {initialsOf(name)}
            </span>
            <span className={`w-full truncate text-sm ${present ? 'font-semibold text-emerald-900' : 'text-gray-700'}`} title={name}>
                {name}
            </span>
        </button>
    );
};

// "+" tile that expands into a small inline form for a first-time visitor.
export const AddGuestTile = ({ onAdd }) => {
    const [open, setOpen] = React.useState(false);
    const [name, setName] = React.useState('');
    const [phoneNumber, setPhoneNumber] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const nameRef = React.useRef(null);

    React.useEffect(() => {
        if (open && nameRef.current) nameRef.current.focus();
    }, [open]);

    const reset = () => {
        setOpen(false); setName(''); setPhoneNumber(''); setError('');
    };

    const save = async () => {
        if (!name.trim()) {
            setError('Enter a name');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await onAdd({ name: name.trim(), phoneNumber: phoneNumber.trim() });
            reset();
        } catch (e) {
            setError(e?.message || 'Could not add guest. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); save(); }
        if (e.key === 'Escape') { e.preventDefault(); reset(); }
    };

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 px-2 py-3 text-gray-500 hover:border-emerald-400 hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100" aria-hidden="true">
                    <PlusIcon className="h-5 w-5" />
                </span>
                <span className="text-sm font-medium">Add guest</span>
            </button>
        );
    }

    return (
        <div className="col-span-2 rounded-xl border-2 border-emerald-400 bg-white p-3 sm:col-span-2" role="group" aria-label="Add a guest">
            <label className="block text-xs font-medium text-gray-700" htmlFor="new-guest-name">Name</label>
            <input
                id="new-guest-name"
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={onKeyDown}
                className="mt-1 block w-full rounded-md border-0 px-2 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-emerald-600"
            />
            <label className="mt-2 block text-xs font-medium text-gray-700" htmlFor="new-guest-phone">Phone (optional)</label>
            <input
                id="new-guest-phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onKeyDown={onKeyDown}
                className="mt-1 block w-full rounded-md border-0 px-2 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-emerald-600"
            />
            {error && <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
            <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={reset} className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                    {saving ? 'Adding…' : 'Add as present'}
                </button>
            </div>
        </div>
    );
};

const AttendanceGrid = ({ title, icon, people, onToggle, onSetAll, emptyText, children }) => {
    const presentCount = people.filter((p) => p.present).length;
    const allPresent = people.length > 0 && presentCount === people.length;
    const headingId = `attendance-${String(title).toLowerCase().replace(/\W+/g, '-')}`;

    return (
        <section className="bg-white shadow-lg rounded-lg overflow-hidden" aria-labelledby={headingId}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-2">
                    {icon}
                    <h2 id={headingId} className="text-lg font-semibold text-gray-900">{title}</h2>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700" aria-live="polite">
                        {presentCount} / {people.length} present
                    </span>
                </div>
                {people.length > 0 && onSetAll && (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => onSetAll(true)}
                            disabled={allPresent}
                            className="rounded-md border border-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                        >
                            Mark all present
                        </button>
                        <button
                            type="button"
                            onClick={() => onSetAll(false)}
                            disabled={presentCount === 0}
                            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                            Clear
                        </button>
                    </div>
                )}
            </div>
            <div className="p-4">
                {people.length === 0 && !children ? (
                    <p className="py-6 text-center text-gray-600">{emptyText}</p>
                ) : (
                    <>
                        {people.length === 0 && <p className="mb-3 text-sm text-gray-600">{emptyText}</p>}
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                            {people.map((person) => (
                                <PersonTile key={person.id} person={person} onToggle={onToggle} />
                            ))}
                            {children}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

AttendanceGrid.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    people: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        name: PropTypes.string,
        present: PropTypes.bool,
    })).isRequired,
    onToggle: PropTypes.func.isRequired,
    onSetAll: PropTypes.func,
    emptyText: PropTypes.string,
    children: PropTypes.node,
};

AttendanceGrid.defaultProps = {
    icon: null,
    onSetAll: null,
    emptyText: 'No one to show',
    children: null,
};

// Sticky bar with the running totals and the submit button.
export const ReportSubmitBar = ({ membersPresent, membersTotal, guestsPresent, label, disabled, onSubmit }) => (
    <div className="sticky bottom-0 z-10 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-700" aria-live="polite">
                <span className="font-semibold text-gray-900">{membersPresent}</span> of {membersTotal} members
                {' · '}
                <span className="font-semibold text-gray-900">{guestsPresent}</span> guest{guestsPresent === 1 ? '' : 's'}
            </p>
            <button
                type="button"
                onClick={onSubmit}
                disabled={disabled}
                className="rounded-lg bg-emerald-500 px-4 py-2 font-semibold text-white shadow-md transition-colors hover:bg-emerald-600 active:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
                {label}
            </button>
        </div>
    </div>
);

export default AttendanceGrid;
